import React from 'react';
import { Card, CardContent } from './ui/card';
import { FileText, Package, Cog, CheckCircle2 } from 'lucide-react';

export const ProcessTimeline = () => {
  const steps = [
    {
      number: '01',
      icon: FileText,
      title: 'Consultation & Blueprinting',
      description: 'We begin with a comprehensive analysis of your project requirements, creating detailed technical blueprints and specifications that ensure precision from the start. Our approach prioritizes clear communication and thorough planning.'
    },
    {
      number: '02',
      icon: Package,
      title: 'Material Sourcing',
      description: 'Our procurement team sources premium-grade materials from certified suppliers, ensuring compliance with industry standards and project specifications. We leverage strong supplier relationships to access the best materials available.'
    },
    {
      number: '03',
      icon: Cog,
      title: 'Precision Fabrication',
      description: 'Certified welders and state-of-the-art equipment combine to execute your project with meticulous attention to detail, tolerances, and structural integrity. Our technical expertise ensures quality at every stage.'
    },
    {
      number: '04',
      icon: CheckCircle2,
      title: 'Quality Assurance Testing',
      description: 'Rigorous multi-point inspection protocols verify weld quality, dimensional accuracy, and compliance before delivery. Every project undergoes comprehensive QA to meet the highest standards of excellence.'
    }
  ];

  return (
    <section id="process" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="fade-in text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-bold text-secondary tracking-wider uppercase mb-4">Our Process</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-primary mb-6">
            Engineering Excellence, Step by Step
          </h2>
          <p className="text-lg text-foreground-muted">
            From initial consultation to final delivery, our comprehensive methodology ensures exceptional results for every project.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Timeline Line - Desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border-light transform -translate-x-1/2"></div>

          <div className="space-y-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`fade-in relative grid lg:grid-cols-2 gap-8 items-center ${
                    isEven ? '' : 'lg:text-right'
                  }`}
                >
                  {/* Content Card */}
                  <div className={isEven ? 'lg:col-start-1' : 'lg:col-start-2'}>
                    <Card className="shadow-soft hover:shadow-elegant transition-shadow duration-300 border-border-light">
                      <CardContent className="p-8">
                        <div className={`flex items-start gap-4 ${
                          isEven ? '' : 'lg:flex-row-reverse lg:text-right'
                        }`}>
                          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                            <Icon className="w-7 h-7 text-primary-foreground" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-4xl font-serif font-bold text-accent opacity-60">
                                {step.number}
                              </span>
                              <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary">
                                {step.title}
                              </h3>
                            </div>
                            <p className="text-foreground-muted leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Timeline Dot - Desktop */}
                  <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-secondary border-4 border-background z-10"></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Process Image */}
        <div className="fade-in mt-16 max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-elegant">
            <img
              src="https://images.unsplash.com/photo-1738162837438-92ff852619a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwcmVjaXNpb24lMjBlbmdpbmVlcmluZ3xlbnwwfHx8fDE3Njk2ODk1NDB8MA&ixlib=rb-4.1.0&q=85"
              alt="Precision engineering and quality inspection"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/40 to-transparent"></div>
            <div className="absolute bottom-8 left-8 text-primary-foreground">
              <p className="text-sm font-medium mb-2">State-of-the-art Facility</p>
              <p className="text-2xl font-serif font-bold">Precision in Every Detail</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
